import { SOURCE_EXCERPT_CONTRACT_VERSION, SOURCE_EXCERPT_COORDINATE_SPACE, SOURCE_EXCERPT_PAGE_NUMBER_BASE } from './activityBatchConversion.constants';

export const SOURCE_DOCUMENT_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
};

// Ninguno de estos estados bloquea la revisión de la actividad.
export const EXCERPT_STATE = {
  MISSING: 'missing',
  UNSUPPORTED: 'unsupported',
  LOADING: 'loading',
  FILE_UNAVAILABLE: 'file_unavailable',
  HASH_MISMATCH: 'hash_mismatch',
  INVALID_BBOX: 'invalid_bbox',
  PAGE_OUT_OF_RANGE: 'page_out_of_range',
  RENDER_ERROR: 'render_error',
  READY: 'ready',
};

export const EXCERPT_TARGET_WIDTH = 900;
const EXCERPT_MIN_SCALE = 1;
const EXCERPT_MAX_SCALE = 4;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const clamp01 = (value) => clamp(value, 0, 1);
// Math.max(1, NaN) es NaN, y un canvas con dimensión NaN queda a 0 px.
const toPixels = (value) => (Number.isFinite(value) ? value : 0);

export function isSupportedSourceExcerpt(excerpt) {
  return (
    excerpt?.contract_version === SOURCE_EXCERPT_CONTRACT_VERSION &&
    excerpt.coordinate_space === SOURCE_EXCERPT_COORDINATE_SPACE &&
    Number(excerpt.page_number_base) === SOURCE_EXCERPT_PAGE_NUMBER_BASE &&
    Array.isArray(excerpt.regions) &&
    excerpt.regions.length > 0
  );
}

// bbox = [x, y, width, height] normalizado en [0,1] con origen arriba a la izquierda. Se tolera que
// sobresalga de la página (luego se recorta), pero no un tamaño nulo/negativo ni quedar completamente fuera.
export function isValidExcerptBbox(bbox) {
  if (!Array.isArray(bbox) || bbox.length !== 4 || !bbox.every(Number.isFinite)) return false;

  const [x, y, width, height] = bbox;
  if (width <= 0 || height <= 0) return false;
  if (x >= 1 || y >= 1) return false;

  return x + width > 0 && y + height > 0;
}

export function isPageInDocument(page, numPages) {
  return Number.isInteger(page) && page >= SOURCE_EXCERPT_PAGE_NUMBER_BASE && page <= (Number(numPages) || 0);
}

// Se garantiza 1 px por lado: un canvas de 0 px no se puede dibujar.
export function getExcerptRegionRect(bbox, pageWidth, pageHeight) {
  const [x, y, width, height] = bbox;
  const left = Math.floor(clamp01(x) * pageWidth);
  const top = Math.floor(clamp01(y) * pageHeight);
  const right = Math.ceil(clamp01(x + width) * pageWidth);
  const bottom = Math.ceil(clamp01(y + height) * pageHeight);

  return {
    x: toPixels(left),
    y: toPixels(top),
    width: Math.max(1, toPixels(right - left)),
    height: Math.max(1, toPixels(bottom - top)),
  };
}

export function getExcerptRenderScale(bbox, pageWidth, targetWidth = EXCERPT_TARGET_WIDTH) {
  const [x, , width] = bbox;
  const visibleWidth = (clamp01(x + width) - clamp01(x)) * pageWidth;
  if (!Number.isFinite(visibleWidth) || visibleWidth <= 0) return EXCERPT_MIN_SCALE;

  return clamp(targetWidth / visibleWidth, EXCERPT_MIN_SCALE, EXCERPT_MAX_SCALE);
}

export const normalizeSha256 = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

// Sin hash esperado no hay nada que comparar, y con `actual` nulo el navegador no pudo calcularlo
// (Web Crypto solo existe en contexto seguro): en ambos casos no se puede afirmar que el documento
// haya cambiado, así que el recorte se muestra.
export function isSameSourceSha256(expected, actual) {
  const wanted = normalizeSha256(expected);
  if (!wanted || actual === null || actual === undefined) return true;

  return wanted === normalizeSha256(actual);
}

// Las regiones se evalúan una a una después: un bbox inválido o una página inexistente no
// invalidan a las demás.
export function getSourceExcerptState(sourceDocument, excerpt) {
  if (!excerpt) return EXCERPT_STATE.MISSING;
  if (!isSupportedSourceExcerpt(excerpt)) return EXCERPT_STATE.UNSUPPORTED;

  const status = sourceDocument?.status;
  if (status === SOURCE_DOCUMENT_STATUS.UNAVAILABLE) return EXCERPT_STATE.FILE_UNAVAILABLE;
  if (status !== SOURCE_DOCUMENT_STATUS.READY) return EXCERPT_STATE.LOADING;
  if (!isSameSourceSha256(excerpt.source_sha256, sourceDocument.sha256)) return EXCERPT_STATE.HASH_MISMATCH;

  return EXCERPT_STATE.READY;
}

export function getExcerptRegionState(region, numPages) {
  if (!isValidExcerptBbox(region?.bbox)) return EXCERPT_STATE.INVALID_BBOX;
  if (!isPageInDocument(region?.page, numPages)) return EXCERPT_STATE.PAGE_OUT_OF_RANGE;

  return EXCERPT_STATE.READY;
}
